import { __decorate } from "tslib";
import { mixins } from 'vue-class-component';
import { Component, Prop } from 'vue-property-decorator';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import ActionButton from '@/components/action_button/Index.vue';
import FormHeader from '@/components/v2/FormHeader.vue';
import NumericInput from '@/components/v2/NumericInput.vue';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import { required, numeric, decimal, requiredIf } from 'vuelidate/lib/validators';
import { requiredProperty, uniqueProperty } from '@/helpers/mixins/LocalizationHelper';
import { Getter } from 'vuex-class';
import { selectOptionBuilder } from '@/helpers/select_option_builder';
import { PromoType } from '@/helpers/entities/enums/v2/PromoType';
import { TaxType } from '@/helpers/entities/enums/v2/TaxType';
import Freebies from '@/components/v2/ecommerce/Freebies';
import { CartPromoProductInputModel } from '@/helpers/entities/models/CartPromoProductInputModel';
import RouterParamsAppMixin from '../../helpers/vue_mixins/RouterParamsAppMixin';
import ViewportSizeMixin from '../../helpers/mixins/ViewportSizeMixin';
let CartPromoForm = class CartPromoForm extends mixins(FormMixin, RouterParamsAppMixin, ViewportSizeMixin) {
    constructor() {
        super(...arguments);
        this.date = null;
        this.form = {
            requiredAmount: null,
            promoType: PromoType.freebies,
            discountType: null,
            value: null,
            freebies: [],
            paymentOptions: null,
        };
    }
    get promoTypes() {
        return [selectOptionBuilder(PromoType.freebies)];
    }
    get discountTypes() {
        return [
            selectOptionBuilder(TaxType.absolute, 'Absolute'),
            selectOptionBuilder(TaxType.percentage),
        ];
    }
    get paymentOptionTypes() {
        return [
            selectOptionBuilder('to_all', 'All'),
            selectOptionBuilder('cod', 'Cash On Delivery'),
            selectOptionBuilder('bank', 'Bank Deposit'),
            selectOptionBuilder('bpi', 'BPI'),
            selectOptionBuilder('gcash', 'GCash'),
            selectOptionBuilder('credit_card', 'Card Payments (Mastercard, VISA, JCB)'),
        ];
    }
    syncedFreebies(dataFreebies) {
        return (dataFreebies?.map((freebie) => {
            const productFreebie = this.form.freebies.find((f) => f.name === freebie.name);
            const val = productFreebie?.uuid || freebie.uuid;
            return selectOptionBuilder(val, freebie.name);
        }) || []);
    }
    get promoIsDiscount() {
        return this.$v.form.promoType.$model === PromoType.discount;
    }
    get promoIsFreebie() {
        return this.$v.form.promoType.$model === PromoType.freebies;
    }
    get discountTypeIsPercentage() {
        return this.$v.form.discountType.$model === TaxType.percentage;
    }
    resetDiscountValue() {
        this.$v.form.value.$model = null;
    }
    get action() {
        return this.update ? 'Edit' : 'Create';
    }
    isDiscount(promoType) {
        return promoType === PromoType.discount;
    }
    freebieErrors(v) {
        const errors = [];
        if (!v.$dirty) {
            return errors;
        }
        if (!v.required) {
            const msg = requiredProperty('Product Freebie');
            errors.push(msg);
        }
        if (!v.unique) {
            const msg = uniqueProperty('Product Freebie');
            errors.push(msg);
        }
        return errors;
    }
    generateFreebie() {
        const freebie = new CartPromoProductInputModel();
        this.form.freebies.push(freebie);
    }
    get undeletedFreebieUuids() {
        const undeleted = (freebie) => {
            return !freebie.isDeleted;
        };
        return this.form.freebies.filter(undeleted).map((freebie) => {
            return freebie.uuid;
        });
    }
    get deletedFreebieUuids() {
        const deleted = (freebie) => {
            return freebie.isDeleted && !!freebie.persistent;
        };
        return this.form.freebies.filter(deleted).map((freebie) => {
            return freebie.uuid;
        });
    }
    freebieExists(val) {
        const uuids = this.undeletedFreebieUuids;
        const one = 1;
        const matches = uuids.filter((uuid) => val === uuid);
        return matches.length > one;
    }
    get promoTypeErrors() {
        const errors = [];
        if (!this.$v.form.promoType.$dirty) {
            return errors;
        }
        if (!this.$v.form.promoType.required) {
            const msg = requiredProperty('Promo Type');
            errors.push(msg);
        }
        return errors;
    }
    get discountTypeErrors() {
        const errors = [];
        if (!this.$v.form.discountType.$dirty) {
            return errors;
        }
        if (!this.$v.form.discountType.required) {
            const msg = requiredProperty('Discount Type');
            errors.push(msg);
        }
        return errors;
    }
    get discountErrors() {
        const errors = [];
        if (!this.$v.form.value.$dirty) {
            return errors;
        }
        if (!this.$v.form.value.required) {
            const msg = requiredProperty('Discount');
            errors.push(msg);
        }
        return errors;
    }
    get requiredAmountErrors() {
        const errors = [];
        if (!this.$v.form.requiredAmount.$dirty) {
            return errors;
        }
        if (!this.$v.form.requiredAmount.required) {
            const msg = requiredProperty('Required Amount');
            errors.push(msg);
        }
        return errors;
    }
    onSubmit() {
        this.$v.$touch();
        if (!this.$v.$invalid) {
            this.$emit('submit', this.form);
        }
    }
    mounted() {
        if (this.formData) {
            this.form = { ...this.formData };
        }
    }
};
__decorate([
    Getter('requestOnFlight')
], CartPromoForm.prototype, "mutationRequestOnFlight", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], CartPromoForm.prototype, "update", void 0);
__decorate([
    Prop({ type: Object, default: null })
], CartPromoForm.prototype, "formData", void 0);
__decorate([
    Prop({ type: String })
], CartPromoForm.prototype, "regionCode", void 0);
CartPromoForm = __decorate([
    Component({
        components: {
            ActionButton,
            FormHeader,
            NumericInput,
            BaseTextField,
            Freebies,
        },
        validations() {
            const vm = this;
            const promoTypeIsDiscount = () => vm.form.promoType === PromoType.discount;
            const promoTypeIsFreebie = () => vm.form.promoType === PromoType.freebies;
            const validations = {
                form: {
                    paymentOptions: {
                        required,
                    },
                    requiredAmount: {
                        required,
                        decimal,
                    },
                    promoType: {
                        required,
                    },
                    discountType: {
                        required: requiredIf(promoTypeIsDiscount),
                    },
                    value: {
                        numeric,
                        required: requiredIf(promoTypeIsDiscount),
                    },
                    freebies: {
                        required: requiredIf(promoTypeIsFreebie),
                        $each: {
                            uuid: {
                                required: requiredIf((model) => {
                                    return !model.isDeleted;
                                }),
                                unique: (value, model) => {
                                    if (!value || model.isDeleted) {
                                        return true;
                                    }
                                    return !vm.freebieExists(value);
                                },
                            },
                        },
                    },
                },
            };
            return validations;
        },
    })
], CartPromoForm);
export default CartPromoForm;
